import React, { useState, useEffect } from 'react';
import { Calendar, Upload, Download, FileSpreadsheet, CheckCircle, X, FileText } from 'lucide-react';
import axios from '../../services/axios';
import { 
  message, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  DatePicker,
  Card, 
  Row, 
  Col, 
  Tag, 
  Space, 
  Tooltip,
  Typography,
} from "antd";
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CalendarOutlined  } from "@ant-design/icons";

const { Title, Text } = Typography;

const documentCategories = [
  "Drawing (DWG)", "Specification (SPF)", "Material Approval Submittal (MAS)",
  "Material Inspection Report (MIR)", "Installation Inspection Report (IIR)",
  "Request for Information (RFI)", "Health, Safety & Environment (HSE)",
  "Non-Conformance Report (NCR)", "Site Observation (SOB)",
  "Interim Payment Application (IPA)", "Interim Payment Certificate (IPC)",
  "Payment Receipt (PYR)", "Method Statement (MST)", "Inspection & Test Plan (ITP)",
  "Tender Drawing (DWG)", "Tender Specification (TSPF)", "Other Tender Documents (TOD)", 
  "Letter of Award (LOA)", "Performance Bond (PBG)", "Advance Payment Bond (ABG)", 
  "Insurance (INS)", "Current Agreement (CAG)", "IFC Drawing (DWG)",
  "IFC Specification (ISPF)", "Shop Drawing (DWG)", "Pre Qualification Document (PQD)",
  "Project Schedule (PRM)", "Authority Approvals (AAP)", "Correspondences (LTR)",
  "Minutes of Meeting (MOM)", "Transmittals (TRN)", "Final Payment Application (FPA)", 
  "Final Payment Certification (fpc)", "Site Photos (SPO)", "As-Built Drawing (ASB)",
  "Operation & Maintenance Manuals (ORM)", "Spares (SPR)", "Warranty (WTY)",
  "Taking-Over Certification (TOC)", "Performance Certificate (PRC)", "Engineer's Instruction (EIN)",
  "Variation Order Request / Change Request (VAR)", "Impact Assessment (IMA)", "Updated Project Document (UPD)",
  "Change Log (CLO)", "Change Control Meeting Minutes (CMO)", "Claim (CLM)", "Variation-to-Contract (VTC)",
  "Change Order (CHO)"
];

const documentTypes = [
  "Tender (TDR)", "Issued for Construction (IFC)", "Construction (CNS)",
  "As-Built (ASB)", "Photo (PIC)", "Commercial (COM)", "Contract (CTR)",
  "Project Close-out (PXX)", "Change Management (CHM)"
];

const disciplines = [
  "Architectural", "BMS", "CCTV", "Coordination", "Civil", "Electrical", 
  "Finishes", "Fire Fighting", "Fire Alarm", "General", "HVAC", "IT", 
  "ID Works", "Mechanical", "Plumbing", "Provisional Sum Items", "Structural", "Telecom", "General"
];

const ScheduleManagement = () => {
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [bulkUploadStep, setBulkUploadStep] = useState(1);
  const [excelFiles, setExcelFiles] = useState([]);
  const [excelDragActive, setExcelDragActive] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadErrors, setUploadErrors] = useState([]);
  const [errors, setErrors] = useState({});
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const projectName = JSON.parse(localStorage.getItem('Project'))

  const bulkUploadSteps = [
    { id: 1, title: 'Download Template', icon: Download, status: 'completed' },
    { id: 2, title: 'Upload Excel', icon: FileSpreadsheet, status: 'current' },
    { id: 3, title: 'File Details', icon: FileText, status: 'pending' },
    // { id: 4, title: 'Review & Submit', icon: CheckCircle, status: 'pending' }
  ];

  const getAllSchedules = async (projectName) => {
    if (!projectName) {
      setSchedules([]);
      setTableLoading(false);
      return;
    }

    setTableLoading(true);
    try {
      const res = await axios.post("get-all-schedules", { user: localStorage.getItem('user'), projectName });
      if (res && res.data && res.data.success && Array.isArray(res.data.fileDetails)) {
        let allFileDetails = res.data.fileDetails;
        const mappedSchedules = allFileDetails.map(schedule => ({
          ...schedule,
          category: Array.isArray(schedule.fileCategory) ? schedule.fileCategory : [schedule.fileCategory],
          type: Array.isArray(schedule.fileType) ? schedule.fileType : [schedule.fileType],
          description: schedule.fileDescription, 
          reference: schedule.fileReference,
          fileReference: schedule.fileReference?.trim(), // Ensure fileReference is trimmed
        }));
        setSchedules(mappedSchedules);
      } else {
        message.warning(res.data?.message || 'No schedules found');
        setSchedules([]);
      }
    } catch (err) {
      message.error(err.response?.data?.msg || "Failed to fetch schedules");
      setSchedules([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (projectName) {
      getAllSchedules(projectName);
    }
  }, [projectName]);

  const downloadExcelTemplate = () => {
    try {
      const headers = [
        'Project Name',
        'Document Category',
        'Document Type',
        'Discipline',
        'Description',
        'Reference',
        'Planned Number Of Documents',
        'Planned Start Date (DD-MM-YYYY)',
        'Planned End Date (DD-MM-YYYY)',
        'Planned Duration',
        'Total Float',
        'Free Float',
        'Remaining Duration',
        'Early Start (DD-MM-YYYY)',
        'Early Finish (DD-MM-YYYY)',
        'Late Start (DD-MM-YYYY)',
        'Late Finish (DD-MM-YYYY)',
      ];

      const sampleData = [
        {
          'Project Name': `Fixed value: ${projectName}`,
          'Document Category': `Valid values: ${documentCategories.join(', ')}`,
          'Document Type': `Valid values: ${documentTypes.join(', ')}`,
          'Discipline': `Valid values: ${disciplines.join(', ')}`,
          'Description': 'Enter description here',
          'Reference': 'Enter reference (e.g., REF-001)',
          'Planned Number Of Documents': 'Enter number of documents (e.g., 10)',
          'Planned Start Date (DD-MM-YYYY)': 'Enter start date (e.g., 01-01-2025)',
          'Planned End Date (DD-MM-YYYY)': 'Enter end date (e.g., 31-12-2025)',
          'Planned Duration': 'Enter duration in days (e.g., 30)',
          'Total Float': 'Enter total float in days (e.g., 5)',
          'Free Float': 'Enter free float in days (e.g., 2)',
          'Remaining Duration': 'Enter remaining duration in days (e.g., 20)',
          'Early Start (DD-MM-YYYY)': 'Enter early start date (e.g., 01-01-2025)',
          'Early Finish (DD-MM-YYYY)': 'Enter early finish date (e.g., 31-12-2025)',
          'Late Start (DD-MM-YYYY)': 'Enter late start date (e.g., 05-01-2025)',
          'Late Finish (DD-MM-YYYY)': 'Enter late finish date (e.g., 04-01-2025)',
        },
      ];

      const ws = XLSX.utils.json_to_sheet(sampleData, { header: headers, skipHeader: false });
      ws['!cols'] = headers.map((_, index) => ({ wch: index < 4 ? 40 : 30 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bulk Schedule Template');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      saveAs(blob, 'bulk_schedule_template.xlsx');
      message.success('Excel template downloaded successfully!');
      setBulkUploadStep(1);
    } catch (err) {
      message.error('Failed to download template');
      console.error('Download Error:', err);
    }
  };

  const handleExcelFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Math.random().toString(36).substr(2, 9)
    }));
    setExcelFiles(newFiles.slice(0, 1));
    if (errors.excelFile) {
      setErrors(prev => ({ ...prev, excelFile: '' }));
    }
  };

  const handleDragEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setExcelDragActive(true);
    } else if (e.type === "dragleave") {
      setExcelDragActive(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExcelDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleExcelFiles(e.dataTransfer.files);
    }
  };

  const removeExcelFile = (id) => {
    setExcelFiles(excelFiles.filter(file => file.id !== id));
    setBulkUploadStep(2);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateExcelData = (data) => {
    const requiredHeaders = [
      'Project Name',
      'Document Category',
      'Document Type',
      'Discipline',
      'Description',
      'Reference',
      'Planned Number Of Documents',
      'Planned Start Date (DD-MM-YYYY)',
      'Planned End Date (DD-MM-YYYY)',
      'Planned Duration',
      'Total Float',
      'Free Float',
      'Remaining Duration',
      'Early Start (DD-MM-YYYY)',
      'Early Finish (DD-MM-YYYY)',
      'Late Start (DD-MM-YYYY)',
      'Late Finish (DD-MM-YYYY)',
    ];

    const headers = Object.keys(data[0] || {});
    const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
    if (missingHeaders.length > 0) {
      return { error: `Missing required headers: ${missingHeaders.join(', ')}` };
    }

    // Filter out sample/template rows
    const validData = data.filter(row => 
      !row['Project Name']?.startsWith('Valid values:') &&
      !row['Document Category']?.startsWith('Valid values:') &&
      !row['Document Type']?.startsWith('Valid values:') &&
      !row['Discipline']?.startsWith('Valid values:') &&
      row['Description'] !== 'Enter description here' &&
      row['Reference'] !== 'Enter reference (e.g., REF-001)' &&
      row['Planned Number Of Documents'] !== 'Enter number of documents (e.g., 10)' &&
      row['Planned Start Date (DD-MM-YYYY)'] !== 'Enter start date (e.g., 01-01-2025)' &&
      row['Planned End Date (DD-MM-YYYY)'] !== 'Enter end date (e.g., 31-12-2025)' &&
      row['Planned Duration'] !== 'Enter duration in days (e.g., 30)' &&
      row['Total Float'] !== 'Enter total float in days (e.g., 5)' &&
      row['Free Float'] !== 'Enter free float in days (e.g., 2)' &&
      row['Remaining Duration'] !== 'Enter remaining duration in days (e.g., 20)' &&
      row['Early Start (DD-MM-YYYY)'] !== 'Enter early start date (e.g., 01-01-2025)' &&
      row['Early Finish (DD-MM-YYYY)'] !== 'Enter early finish date (e.g., 31-12-2025)' &&
      row['Late Start (DD-MM-YYYY)'] !== 'Enter late start date (e.g., 05-01-2025)' &&
      row['Late Finish (DD-MM-YYYY)'] !== 'Enter late finish date (e.g., 04-01-2025)'
    );

    const errors = [];

    for (let i = 0; i < validData.length; i++) {
      const row = validData[i];

      // Helper function to parse Excel dates
      const parseExcelDate = (value, field) => {
        if (!value) {
          return { error: `${field} is missing or empty` };
        }
        if (typeof value === 'number') {
          // Handle Excel serial date (1900 date system)
          try {
            const date = XLSX.SSF.parse_date_code(value);
            const formattedDate = moment({
              year: date.y,
              month: date.m - 1, // moment months are 0-based
              day: date.d,
            }).format('DD-MM-YYYY');
            if (!moment(formattedDate, 'DD-MM-YYYY', true).isValid()) {
              return { error: `${field} has an invalid date (Excel serial date)` };
            }
            return { value: formattedDate };
          } catch (e) {
            return { error: `${field} has an invalid Excel serial date` };
          }
        }
        // Handle string dates
        if (typeof value === 'string' && moment(value, 'DD-MM-YYYY', true).isValid()) {
          return { value };
        }
        return { error: `${field} must be in DD-MM-YYYY format (e.g., 04-01-2025)` };
      };

      // Validate fields
      if (!row['Project Name'] || row['Project Name'] !== projectName) {
        errors.push(
          `Invalid Project Name in row ${i + 2}. Must be "${projectName}"`
        );
      }
      if (!row['Document Category'] || !documentCategories.includes(row['Document Category'])) {
        errors.push(`Invalid or missing Document Category in row ${i + 2}`);
      }
      if (!row['Document Type'] || !documentTypes.includes(row['Document Type'])) {
        errors.push(`Invalid or missing Document Type in row ${i + 2}`);
      }
      if (!row['Discipline'] || !disciplines.includes(row['Discipline'])) {
        errors.push(`Invalid or missing Discipline in row ${i + 2}`);
      }
      if (!row['Description'] || row['Description'].toString().trim() === '') {
        errors.push(`Invalid or missing Description in row ${i + 2}`);
      }
      if (!row['Reference'] || row['Reference'].toString().trim() === '') {
        errors.push(`Invalid or missing Reference in row ${i + 2}`);
      }
      if (!row['Planned Number Of Documents'] || isNaN(row['Planned Number Of Documents']) || row['Planned Number Of Documents'] < 0) {
        errors.push(`Invalid or missing Planned Number Of Documents in row ${i + 2}`);
      }
      if (!row['Planned Duration'] || isNaN(row['Planned Duration']) || row['Planned Duration'] < 0) {
        errors.push(`Invalid or missing Planned Duration in row ${i + 2}`);
      }
      if (row['Total Float'] === undefined || row['Total Float'] === null || isNaN(row['Total Float'])) {
        errors.push(`Invalid or missing Total Float in row ${i + 2}`);
      }
      if (row['Free Float'] === undefined || row['Free Float'] === null || isNaN(row['Free Float'])) {
        errors.push(`Invalid or missing Free Float in row ${i + 2}`);
      }
      if (!row['Remaining Duration'] || isNaN(row['Remaining Duration']) || row['Remaining Duration'] < 0) {
        errors.push(`Invalid or missing Remaining Duration in row ${i + 2}`);
      }

      // Validate dates
      const dateFields = [
        { key: 'Planned Start Date (DD-MM-YYYY)', field: 'Planned Start Date' },
        { key: 'Planned End Date (DD-MM-YYYY)', field: 'Planned End Date' },
        { key: 'Early Start (DD-MM-YYYY)', field: 'Early Start' },
        { key: 'Early Finish (DD-MM-YYYY)', field: 'Early Finish' },
        { key: 'Late Start (DD-MM-YYYY)', field: 'Late Start' },
        { key: 'Late Finish (DD-MM-YYYY)', field: 'Late Finish' },
      ];

      for (const { key, field } of dateFields) {
        const result = parseExcelDate(row[key], field);
        if (result.error) {
          errors.push(`Row ${i + 2}: ${result.error}`);
        } else {
          row[key] = result.value; // Update row with formatted date
        }
      }
    }

    if (errors.length > 0) {
      return { error: errors.join('; ') };
    }

    return { validData };
  };

  const handleValidateExcel = async () => {
    if (excelFiles.length === 0) {
      setErrors((prev) => ({ ...prev, excelFile: "Please select an Excel file to upload" }));
      message.error("Please select an Excel file to upload");
      return false;
    }

    setUploadLoading(true);
    setUploadErrors([]);

    try {
      const file = excelFiles[0].file;
      const reader = new FileReader();
      return new Promise((resolve) => {
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
              setErrors((prev) => ({ ...prev, excelFile: "Excel file is empty" }));
              message.error("Excel file is empty");
              setUploadLoading(false);
              resolve(false);
              return;
            }

            const validationResult = validateExcelData(jsonData);
            if (validationResult.error) {
              setErrors((prev) => ({ ...prev, excelFile: validationResult.error }));
              message.error(validationResult.error);
              setUploadLoading(false);
              resolve(false);
              return;
            }

            setErrors((prev) => ({ ...prev, excelFile: '' }));
            setUploadLoading(false);
            resolve(true);
          } catch (err) {
            setErrors((prev) => ({
              ...prev,
              excelFile: "Failed to process Excel file: " + err.message,
            }));
            message.error("Failed to process Excel file: " + err.message);
            setUploadLoading(false);
            resolve(false);
          }
        };
        reader.readAsArrayBuffer(file);
      });
    } catch (err) {
      setUploadLoading(false);
      setErrors((prev) => ({ ...prev, excelFile: "Failed to process Excel file: " + err.message }));
      message.error("Failed to process Excel file: " + err.message);
      return false;
    }
  };

  const handleBulkUpload = async () => {
    setUploadLoading(true);
    setUploadErrors([]);
    setBulkUploadStep(4);

    try {
      // ✅ Get form values
      const formValues = await form.validateFields();

      // ✅ Universal date converter (supports both moment & dayjs)
      const formatDate = (date) => {
        if (!date) return null;
        if (typeof date === "string") {
          if (moment(date, "YYYY-MM-DD", true).isValid()) return date;
          if (moment(date, "DD-MM-YYYY", true).isValid())
            return moment(date, "DD-MM-YYYY").format("YYYY-MM-DD");
        }
        if (date.$d instanceof Date) {
          // dayjs or moment object
          return moment(date.$d).format("YYYY-MM-DD");
        }
        return null;
      };

      // ✅ Cleaned and sanitized file details
      const fileDetails = [
        {
          fileReference: formValues.fileReference?.trim() || "",
          fileDescription: formValues.fileDescription?.trim() || "",
          fileCategory: formValues.fileCategory?.trim() || "",
          fileType: formValues.fileType?.trim() || "",
          startDate: formatDate(formValues.startDate),
          endDate: formatDate(formValues.endDate),
        },
      ];

      // ✅ Field validation
      for (const [key, value] of Object.entries(fileDetails[0])) {
        if (!value) {
          message.error(`Missing or invalid ${key}`);
          setUploadLoading(false);
          return;
        }
      }

      if (excelFiles.length === 0) {
        throw new Error("No Excel file uploaded");
      }

      const file = excelFiles[0].file;
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const validationResult = validateExcelData(jsonData);
          if (validationResult.error) {
            setErrors((prev) => ({ ...prev, excelFile: validationResult.error }));
            message.error(validationResult.error);
            setUploadLoading(false);
            return;
          }

          const { validData } = validationResult;
          setUploadProgress({ current: 0, total: validData.length });

          const payload = validData.map((doc) => ({
            projectName: doc["Project Name"],
            category: [doc["Document Category"]],
            type: [doc["Document Type"]],
            discipline: doc["Discipline"],
            description: doc["Description"],
            reference: doc["Reference"],
            plannedNumberOfDocuments:
              parseInt(doc["Planned Number Of Documents"]) || 0,
            plannedstartDate: moment(
              doc["Planned Start Date (DD-MM-YYYY)"],
              "DD-MM-YYYY"
            ).format("YYYY-MM-DD"),
            plannedendDate: moment(
              doc["Planned End Date (DD-MM-YYYY)"],
              "DD-MM-YYYY"
            ).format("YYYY-MM-DD"),
            plannedduration: parseInt(doc["Planned Duration"]) || 0,
            totalfloat: parseInt(doc["Total Float"]) || 0,
            freefloat: parseInt(doc["Free Float"]) || 0,
            remainingDuration: parseInt(doc["Remaining Duration"]) || 0,
            earlystart: doc["Early Start (DD-MM-YYYY)"]
              ? moment(doc["Early Start (DD-MM-YYYY)"], "DD-MM-YYYY").format(
                  "YYYY-MM-DD"
                )
              : null,
            earlyfinish: doc["Early Finish (DD-MM-YYYY)"]
              ? moment(doc["Early Finish (DD-MM-YYYY)"], "DD-MM-YYYY").format(
                  "YYYY-MM-DD"
                )
              : null,
            latestart: doc["Late Start (DD-MM-YYYY)"]
              ? moment(doc["Late Start (DD-MM-YYYY)"], "DD-MM-YYYY").format(
                  "YYYY-MM-DD"
                )
              : null,
            latefinish: doc["Late Finish (DD-MM-YYYY)"]
              ? moment(doc["Late Finish (DD-MM-YYYY)"], "DD-MM-YYYY").format(
                  "YYYY-MM-DD"
                )
              : null,
          }));

          const response = await axios.post("/bulk-create-schedules", {
            data: payload,
            fileDetails,
            user: localStorage.getItem("user"),
          });

          setUploadProgress({ current: validData.length, total: validData.length });
          message.success("Schedules and file details uploaded successfully!");
          setExcelFiles([]);
          setShowBulkUploadModal(false);
          form.resetFields();

          if (projectName) {
  getAllSchedules(projectName);
}

        } catch (err) {
          console.error("Error reading Excel file:", err);
          message.error("Failed to process Excel file");
        } finally {
          setUploadLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error("❌ Upload error:", err);
      message.error(err.message || "Upload failed");
      setUploadLoading(false);
    }
  };

  const handleValuesChange = (_, allValues) => {
    const requiredFields = [
      'fileReference',
      'fileDescription',
      'fileCategory',
      'fileType',
      'startDate',
      'endDate',
    ];

    const allFilled = requiredFields.every((field) => {
      const value = allValues[field];

      if (field === 'startDate' || field === 'endDate') {
        return value && moment(value).isValid();
      }

      return value !== undefined && value !== null && String(value).trim() !== '';
    });

    setIsFormValid(allFilled);
  };

  const columns = [
    {
      title: 'File Reference',
      dataIndex: 'fileReference',
      key: 'fileReference',
      width: 150,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => {
            navigate(`/home/create-schedule/fileReference/${text}`, {
              state: {
                projectName,
                record: {
                  ...record,
                  fileReference: text?.trim(),
                  category: Array.isArray(record.category)
                    ? record.category
                    : [record.category],
                  type: Array.isArray(record.type)
                    ? record.type
                    : [record.type],
                },
              },
            });
          }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'File Description',
      dataIndex: 'fileDescription',
      key: 'fileDescription',
      ellipsis: { showTitle: false },
      render: (desc) => <Tooltip title={desc}>{desc || '-'}</Tooltip>,
      width: 200,
    },
    {
      title: 'File Category',
      dataIndex: 'fileCategory',
      key: 'fileCategory',
      render: (category) => <Tag color="blue">{category}</Tag>,
      width: 180,
      filters: [...new Set(schedules.map(sch => sch.fileCategory))].map(category => ({
        text: category,
        value: category,
      })),
      onFilter: (value, record) => record.fileCategory === value,
    },
    {
      title: 'File Type',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (type) => <Tag color="green">{type}</Tag>,
      width: 150,
      filters: [...new Set(schedules.map(sch => sch.fileType))].map(type => ({
        text: type,
        value: type,
      })),
      onFilter: (value, record) => record.fileType === value,
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (d) => (d ? moment(d).format('DD/MM/YYYY') : '-'),
      width: 160,
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (d) => (d ? moment(d).format('DD/MM/YYYY') : '-'),
      width: 150,
    }
  ];

  const filteredSchedules = schedules.filter(schedule => {
    const matchesSearch = !searchTerm || 
      schedule.fileDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(schedule.fileCategory) ? schedule.fileCategory.join(', ') : schedule.fileCategory)
        ?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(schedule.fileType) ? schedule.fileType.join(', ') : schedule.fileType)
        ?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !filterCategory || 
      (Array.isArray(schedule.fileCategory) ? 
        schedule.fileCategory.includes(filterCategory) : 
        schedule.fileCategory === filterCategory);
    
    const matchesType = !filterType || 
      (Array.isArray(schedule.fileType) ? 
        schedule.fileType.includes(filterType) : 
        schedule.fileType === filterType);
    
    return matchesSearch && matchesCategory && matchesType;
  });

  const StepIndicator = ({ step, isActive, isCompleted }) => {
    const Icon = step.icon;

    return (
      <div className={`flex items-center ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
          isActive ? 'border-blue-600 bg-blue-50' : 
          isCompleted ? 'border-green-600 bg-green-50' : 
          'border-gray-300 bg-gray-50'
        }`}>
          {isCompleted ? (
            <CheckCircle className="w-6 h-6" />
          ) : (
            <Icon className="w-6 h-6" />
          )}
        </div>
        <span className="ml-3 font-medium">{step.title}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* <div className="max-w-7xl mx-auto">               */}
      <div className="mx-auto w-full px-2 sm:px-4 md:px-6 lg:px-8">
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="text-blue-600" size={32} />
              <div>
                <Title level={2} className="!mb-1">Schedule Management</Title>
                <Text type="secondary">Manage project documents and timelines</Text>
              </div>
            </div>
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              onClick={() => setShowBulkUploadModal(true)}
            >
              Add Schedule
            </Button>
          </div>
        </Card>

        {/* <Card title="Select Project" className="mb-6">
          <Row gutter={[16, 16]} justify="start" wrap>
            {currentProjects.map(project => (
              <Col xs={24} sm={12} md={8} lg={8} key={project.projectCode}>
                <Card
                  hoverable
                  className={`cursor-pointer transition-all duration-200 ${
                    selectedProject === project.projectCode
                      ? "border-blue-500 shadow-md"
                      : "border-gray-200"
                  }`}
                  onClick={() => {
                    setSelectedProject(project.projectCode);
                  }}
                >
                  <Title level={5} className="!mb-2">{project.projectName}</Title>
                  <Text type="secondary" className="block mb-3">
                    Manager: {project.projectManager}
                  </Text>
                  <div className="flex items-center justify-between">
                    <Tag
                      color={project.status === "Active" ? "success" : "warning"}
                    >
                      {project.status}
                    </Tag>
                    <Text type="secondary">{project.documents} schedule</Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-6">
              <Space>
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                
                {[...Array(totalPages)].map((_, index) => (
                  <Button
                    key={index + 1}
                    type={currentPage === index + 1 ? "primary" : "default"}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Button>
                ))}

                <Button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </Space>
            </div>
          )}
        </Card> */}

        <Card>
          {!projectName ? (
            <div className="text-center py-12">
              <Calendar size={48} className="text-gray-400 mx-auto mb-4" />
              <Title level={4} type="secondary">No Project Selected</Title>
              <Text type="secondary">
                Please select a project before opening Schedule Management
              </Text>
            </div>
          ) : (
             <Table
              columns={columns}
              dataSource={filteredSchedules}
              rowKey="_id"
              loading={tableLoading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                defaultPageSize: 10,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              scroll={{ x: 1000 }}
            />
          )}
        </Card>

        <Modal
          title="Create Multiple Schedules"
          open={showBulkUploadModal}
          onCancel={() => {
            setShowBulkUploadModal(false);
            setExcelFiles([]);
            setBulkUploadStep(1);
            setErrors({});
            setUploadErrors([]);
            form.resetFields();
          }}
          footer={null}
          width={800}
        >
          <div className="p-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
              <div className="flex items-center justify-between">
                {bulkUploadSteps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <StepIndicator
                      step={step}
                      isActive={bulkUploadStep === step.id}
                      isCompleted={bulkUploadStep > step.id}
                    />
                    {index < bulkUploadSteps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-4 ${
                          bulkUploadStep > step.id ? "bg-green-600" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {bulkUploadStep === 1 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Excel Template</h3>
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Ready to use</span>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">
                  Download the pre-configured template with all required fields and
                  validation rules.
                </p>
                <Button
                  type="primary"
                  onClick={downloadExcelTemplate}
                  size="large"
                  disabled={loading}
                  style={{
                    width: "100%",
                    height: "48px",
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "12px",
                    background: "linear-gradient(to right, #2563eb, #4f46e5)",
                  }}
                >
                  <span className="flex items-center">
                    <Download className="w-5 h-5 mr-2" />
                    {loading ? "Loading..." : "Download Excel Template"}
                  </span>
                </Button>

                <div className="flex justify-end mt-6">
                  <Button type="primary" onClick={() => setBulkUploadStep(2)}>
                    Next
                  </Button>
                </div>
              </div>
            )}

            {bulkUploadStep === 2 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Upload Excel Metadata
                </h3>

                <div
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                    excelDragActive
                      ? "border-blue-500 bg-blue-50"
                      : errors.excelFile
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onDragEnter={handleDragEvents}
                  onDragLeave={handleDragEvents}
                  onDragOver={handleDragEvents}
                  onDrop={handleFileDrop}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleExcelFiles(e.target.files)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                        excelDragActive
                          ? "bg-blue-100"
                          : errors.excelFile
                          ? "bg-red-100"
                          : "bg-gray-100"
                      }`}
                    >
                      <FileSpreadsheet
                        className={`w-8 h-8 ${
                          excelDragActive
                            ? "text-blue-600"
                            : errors.excelFile
                            ? "text-red-500"
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      {excelDragActive ? "Drop Excel file here" : "Upload Excel file"}
                    </h4>
                    <p className="text-gray-500 mb-4">
                      Drag and drop your completed Excel file or click to browse
                    </p>
                  </div>
                </div>

                {excelFiles.length > 0 && (
                  <div className="mt-4">
                    {excelFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <FileSpreadsheet className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{file.name}</p>
                            <p className="text-sm text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeExcelFile(file.id)}
                          className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {errors.excelFile && (
                  <div className="text-red-500 mt-4">{errors.excelFile}</div>
                )}

                <div className="flex justify-between mt-6">
                  <Button onClick={() => setBulkUploadStep(1)}>Back</Button>
                  <Button
                    type="primary"
                    onClick={async () => {
                      const isValid = await handleValidateExcel();
                      if (isValid) {
                        setBulkUploadStep(3);
                      }
                    }}
                    disabled={excelFiles.length === 0 || uploadLoading}
                    loading={uploadLoading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {bulkUploadStep === 3 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <Title level={5}>File Details</Title>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleBulkUpload}
                  autoComplete="off"
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="File Reference"
                        name="fileReference"
                        rules={[{ required: true, message: 'Please enter File Reference' }]}
                      >
                        <Input placeholder="Enter File Reference" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label="File Description"
                        name="fileDescription"
                        rules={[{ required: true, message: 'Please enter File Description' }]}
                      >
                        <Input placeholder="Enter File Description" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="File Category"
                        name="fileCategory"
                        rules={[{ required: true, message: 'Please select File Category' }]}
                      >
                        <Input placeholder="Enter File Category" />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label="File Type"
                        name="fileType"
                        rules={[{ required: true, message: 'Please select File Type' }]}
                      >
                        <Input placeholder="Enter File Type" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Start Date"
                        name="startDate"
                        rules={[{ required: true, message: 'Please select Start Date' }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        label="End Date"
                        name="endDate"
                        rules={[{ required: true, message: 'Please select End Date' }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <Button type="primary" htmlType="submit" loading={uploadLoading}>
                      Submit
                    </Button>
                  </div>
                </Form>

                {errors.excelFile && <div className="text-red-500 mt-2">{errors.excelFile}</div>}

                <div className="flex justify-between mt-6">
                  <Button onClick={() => setBulkUploadStep(2)}>Back</Button>
                  <Button
                    type="primary"
                    onClick={async () => {
                      try {
                        await form.validateFields();
                        setBulkUploadStep(4);
                      } catch (err) {
                        message.error('Please fill all required fields correctly');
                      }
                    }}
                    disabled={!isFormValid}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {bulkUploadStep === 4 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Ready to Upload
                    </h3>
                    <p className="text-gray-600">
                      {excelFiles.length > 0
                        ? `${excelFiles.length} Excel file ready for processing`
                        : "Please upload an Excel file"}
                    </p>
                  </div>
                  <Button
                    type="primary"
                    onClick={handleBulkUpload}
                    size="large"
                    disabled={loading || uploadLoading || excelFiles.length === 0}
                    style={{
                      padding: "12px 32px",
                      height: "48px",
                      fontSize: "16px",
                      borderRadius: "12px",
                      background:
                        excelFiles.length > 0 && !uploadLoading
                          ? "linear-gradient(to right, #16a34a, #15803d)"
                          : "",
                    }}
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {uploadLoading
                      ? `Uploading ${uploadProgress.current}/${uploadProgress.total}`
                      : "Start Upload"}
                  </Button>
                </div>

                <div className="flex justify-between mt-6">
                  <Button onClick={() => setBulkUploadStep(3)}>Back</Button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ScheduleManagement;